import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';

import { CoverStage, Team, TeaserContainer, Teaser, Container } from '@ccg/fork';

import relatedCasesQuery from '../../../graphql/queries/relatedCases.graphql';

import Embeds from '../../shared/Embeds/Embeds';
import PageTemplate from '../templates/page';
import { encodeMemberSlug } from '../../../helper';

import { LoginContext } from '../../../contexts/LoginProvider';

const Case = ({ data, site }) => {
  if (!data.entry) return null;

  const {
    image,
    fixedStageImage,
    video,
    loop,
    tags,
    solutions,
    industries,
    tasks,
    client,
    title,
    subline,
    excerpt,
    embedsCopy,
    embeds,
    companies,
    coreTeam,
    extendedTeam
  } = data.entry;
  const { relatedCasesCount } = data.teaserSettings;

  const limit = relatedCasesCount || 4;

  const {
    userData: { isGuest }
  } = useContext(LoginContext);

  const { data: relatedData } = useQuery(relatedCasesQuery, {
    variables: {
      site,
      currentId: data.entry.id,
      limit,
      relatedTo: [...solutions, ...industries, ...tasks, ...tags].map(item => parseFloat(item.id))
    }
  });

  const relatedEntries = relatedData && relatedData.relatedEntries;

  const primaryMembers = coreTeam
    .filter(item => {
      // allow if user is logged in or item is enabled
      return item.user[0].status === 'active' || !isGuest;
    })
    .map(({ role, user }) => ({
      id: user[0].id,
      name: user[0].name,
      role: role[0].title,
      status: user[0].status,
      link: {
        href: { pathname: '/user', query: { slug: `${encodeMemberSlug(user[0])}` } },
        asPath: `/team/${encodeMemberSlug(user[0])}`
      }
    }));

  const secondaryMembers = extendedTeam
    .filter(item => {
      // allow if user is logged in or item is enabled
      return item.user[0].status === 'active' || !isGuest;
    })
    .map(({ role, user }) => ({
      id: user[0].id,
      name: user[0].name,
      role: role[0].title,
      status: user[0].status
    }));

  return (
    <PageTemplate
      navigation={data.navigation}
      seo={data.entry}
      footer={data.footer}
      seoDefaults={data.seoDefaults}
      cookieLayer={data.cookieLayer}
    >
      <CoverStage
        image={
          image &&
          image.length > 0 && {
            ...image[0],
            srcset: {
              '1440w': image[0].width1440,
              '960w': image[0].width960,
              '720w': image[0].width720,
              '375w': image[0].width375
            }
          }
        }
        fixedImage={fixedStageImage}
        video={video && video.length > 0 && video[0]}
        loop={loop}
        client={client[0].title}
        headline={title}
        subline={subline}
        excerpt={excerpt}
        tags={tags}
        breadcrumbLink={{
          label: 'Cases',
          href: { pathname: '/standard', query: { uri: 'cases' } },
          asPath: '/cases'
        }}
      />

      <Embeds embedsCopy={embedsCopy} embeds={embeds} />

      <Team
        companies={companies}
        primaryMembers={primaryMembers}
        secondaryMembers={secondaryMembers}
      />

      {relatedEntries && relatedEntries.length > 0 && (
        <Container headline="More cases:">
          <TeaserContainer>
            {relatedEntries.map(item => (
              <Teaser
                key={item.id}
                topic={item.client && item.client.length > 0 && item.client[0].title}
                accentColor={
                  item.client &&
                  item.client.length > 0 && {
                    primary: item.client[0].ciColorPrimary,
                    secondary: item.client[0].ciColorSecondary
                  }
                }
                image={
                  item.image.length > 0 && {
                    ...item.image[0],
                    srcset: {
                      '1440w': item.image[0].width1440,
                      '900w': item.image[0].width900,
                      '720w': item.image[0].width720,
                      '390w': item.image[0].width390
                    }
                  }
                }
                video={item.video.length > 0 && item.video[0]}
                headline={item.title}
                link={{
                  href: { pathname: '/case', query: { slug: item.slug } },
                  asPath: `/${item.uri}`
                }}
                tags={item.tags}
              />
            ))}
          </TeaserContainer>
        </Container>
      )}
    </PageTemplate>
  );
};

Case.propTypes = {
  site: PropTypes.string.isRequired,
  data: PropTypes.shape().isRequired
};

Case.defaultProps = {};

export default Case;
